import { type Editor, type NodeViewProps } from "@tiptap/core";
import { NodeViewWrapper } from "@tiptap/react";
import { useCallback, useEffect, useState } from "react";

import { cn } from "@btnet/utilities-core";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";
import { useRequiredContext } from "helpers/globalContext/useRequiredContext";
import { type IEntityLinkRouterHandler } from "helpers/routing/EntityLinkRouter/EntityLinkRouter.api.handler";
import { type IEntityRecord } from "helpers/routing/EntityLinkRouter/EntityLinkRouter.api.types";

import { reportError } from "utilities/errorHelpers";
import { getCurrentPortalType } from "utilities/portal/portal";
import { routes } from "utilities/routes";

import { EntityLinkOptionsOverlay } from "commonComponents/btWrappers/editor/core/BTEditorContent/overlays/EntityLinkOptionsOverlay/EntityLinkOptionsOverlay";
import {
    deleteNoHistory,
    entityTypeToRelatedItemType,
    fastIsNodeInSelection,
    updateAttributesNoHistory,
} from "commonComponents/btWrappers/editor/core/editor.utils";
import {
    type IEntityLinkAttributes,
    type IEntityLinkStorage,
} from "commonComponents/btWrappers/editor/core/extensions/custom/entityLink/EntityLinkExtension";
import { type IRelatedItemEntityData } from "commonComponents/entity/relatedItem/RelatedItem.types";
import { getRelatedItemEntityData } from "commonComponents/entity/relatedItem/RelatedItem.utilities";

import { EntityType } from "handlers";

import "./EntityLinkNodeView.less";

export interface IEntityMetadata {
    entityRecord: IEntityRecord;
    relatedEntity: IRelatedItemEntityData;
    url: string;
}

export const EntityLinkNodeView = ({
    selected: isSolelySelected,
    editor,
    node,
    extension,
    getPos,
}: NodeViewProps) => {
    const builderInfo = useRequiredContext(BuilderInfoContext);
    const [isWithinTextSelection, setIsWithinTextSelection] = useState<boolean>(false);
    const [metadata, setMetadata] = useState<IEntityMetadata>();

    const handler = extension.options.handler as IEntityLinkRouterHandler;
    const storage = editor.storage.entityLink as IEntityLinkStorage;
    const nodeMetadataStorage = storage.nodeMetadata;
    const attrs = node.attrs as IEntityLinkAttributes;

    const replaceWithLink = useCallback(() => {
        // Convert the node back to a URL, so at least the user
        // can link to it the old way.
        const pos = getPos();
        if (pos === undefined) {
            return;
        }
        let newUrl: string;
        if (attrs.externalId) {
            newUrl = routes.entityLink.getFullLink(attrs.externalId);
        } else {
            newUrl = attrs.url ?? "";
        }

        // Deleting in a chain seems to create a new transaction instead of joining on the
        // existing one. Instead, use the prosemirror transaction directly, which can
        // specifically not add to the history.
        deleteNoHistory(editor, pos, pos + node.nodeSize);

        editor
            .chain()
            .focus()
            .setLink({ href: newUrl, target: "_blank" })
            .insertContentAt(pos, newUrl)
            .run();
    }, [editor, getPos, attrs.externalId, attrs.url, node.nodeSize]);

    const insertMetadata = useCallback(async () => {
        if (!attrs.entityType || !attrs.id || !attrs.jobId) {
            throw new Error("insertMetadata called with missing attributes");
        }

        const linkData = await handler.insert({
            entityType: attrs.entityType,
            id: attrs.id,
            jobId: attrs.jobId,
            organizationId: builderInfo.builderId,
        });

        const entityType = linkData.entityType as keyof typeof entityTypeToRelatedItemType;
        const type = entityTypeToRelatedItemType[entityType];

        if (!type) {
            throw new Error(linkData.entityType + " is not mapped to a RelatedItemType");
        }

        return {
            entityRecord: linkData,
            relatedEntity: getRelatedItemEntityData(type),
            url: routes.entityLink.getFullLink(linkData.externalId),
        } as IEntityMetadata;
    }, [builderInfo.builderId, handler, attrs.entityType, attrs.id, attrs.jobId]);

    const fetchData = useCallback(async () => {
        // If the extension is disabled, we don't pass the handler in the config
        if (!handler || !attrs.externalId) {
            replaceWithLink();
            return;
        }

        const linkData: IEntityRecord = await handler.get(attrs.externalId, getCurrentPortalType());

        const entityType = linkData.entityType as keyof typeof entityTypeToRelatedItemType;
        const type = entityTypeToRelatedItemType[entityType];

        if (!type) {
            throw new Error(linkData.entityType + " is not mapped to a RelatedItemType");
        }

        return {
            entityRecord: linkData,
            relatedEntity: getRelatedItemEntityData(type),
            url: routes.entityLink.getFullLink(linkData.externalId),
        } as IEntityMetadata;
    }, [handler, attrs.externalId, replaceWithLink]);

    useEffect(() => {
        // SPA url handling
        if (!attrs.externalId) {
            if (!attrs.url || !attrs.entityType || !attrs.id || !attrs.jobId) {
                replaceWithLink();
                return;
            }

            insertMetadata()
                .then((response: IEntityMetadata) => {
                    // Capture once and bail if the node was removed from the doc while the request was in flight.
                    const currentPos = getPos();
                    if (currentPos === undefined) {
                        return;
                    }
                    // If the node was deleted while the request was in flight
                    if (!editor.view.state.doc.nodeAt(currentPos)?.eq(node)) {
                        return;
                    }

                    nodeMetadataStorage.set(response.entityRecord.externalId, response);
                    updateAttributesNoHistory(editor, currentPos, {
                        url: undefined,
                        entityType: undefined,
                        id: undefined,
                        jobId: undefined,
                        externalId: response.entityRecord.externalId,
                    });
                    setMetadata(response);
                })
                .catch((e) => {
                    reportError(e);
                    replaceWithLink();
                });
            return;
        }

        // /link URL handling
        if (nodeMetadataStorage.has(attrs.externalId)) {
            if (!metadata) {
                setMetadata(nodeMetadataStorage.get(attrs.externalId));
            }
            return;
        }

        fetchData()
            .then((response: IEntityMetadata | undefined) => {
                if (response && attrs.externalId) {
                    nodeMetadataStorage.set(attrs.externalId, response);
                    setMetadata(response);
                }
            })
            .catch((e) => {
                reportError(e);
                replaceWithLink();
            });
    }, [
        attrs,
        editor,
        fetchData,
        getPos,
        insertMetadata,
        metadata,
        node,
        nodeMetadataStorage,
        replaceWithLink,
    ]);

    useEffect(() => {
        const onSelectionUpdate = ({
            editor: selectionUpdateEditor,
        }: {
            editor: Editor;
            transaction: Editor["state"]["tr"];
        }) => {
            setIsWithinTextSelection(
                fastIsNodeInSelection(
                    selectionUpdateEditor.state.doc,
                    selectionUpdateEditor.state.selection,
                )(node),
            );
        };

        editor.on("selectionUpdate", onSelectionUpdate);

        return () => {
            editor.off("selectionUpdate", onSelectionUpdate);
        };
    }, [editor, isWithinTextSelection, node]);

    const onClick = (event: React.MouseEvent | React.KeyboardEvent) => {
        event.stopPropagation();
        if (
            !editor.isEditable &&
            attrs.externalId &&
            metadata &&
            !metadata.entityRecord.isDeleted
        ) {
            window.open(routes.entityLink.getFullLink(attrs.externalId), "_blank", "noopener");
        }
    };

    if (!metadata) {
        return (
            <NodeViewWrapper
                className="EntityLinkNodeViewWrapper"
                as="span"
                title="loading..."
                data-drag-handle
            >
                {/* Because this is inline and we want the skeleton to still wrap, it 
                has to be text. This is ugly but it works! */}
                <span className="EntityLinkSkeleton">{" ".repeat(32)}</span>
            </NodeViewWrapper>
        );
    }

    const isHighlighted = isSolelySelected || isWithinTextSelection;
    const nodeTitle = `${metadata.entityRecord.title}${
        metadata.entityRecord.isDeleted ? " (Deleted) " : ""
    }`;

    return (
        <NodeViewWrapper
            className={cn("EntityLinkNodeViewWrapper", {
                NotAllowed: metadata.entityRecord.isDeleted && !editor.isEditable,
            })}
            as="span"
            title={metadata.entityRecord.title}
            data-drag-handle
        >
            <span
                data-testid={"related-item-" + attrs.externalId}
                className={cn("EntityLinkContent", {
                    selected: isHighlighted,
                    notSelected: !isHighlighted,
                    deleted: metadata.entityRecord.isDeleted,
                })}
                onClick={onClick}
            >
                {metadata.entityRecord.entityType !== EntityType.ClientUpdate && (
                    <metadata.relatedEntity.RelatedIcon
                        className="EntityLinkContent--Tag"
                        size="auto"
                    />
                )}

                <span>{`${metadata.relatedEntity.name}: `}</span>
                <span className="EntityLinkContent--Title">{nodeTitle}</span>
            </span>
            {isSolelySelected && editor.isEditable && (
                <EntityLinkOptionsOverlay editor={editor} node={node} metadata={metadata} />
            )}
        </NodeViewWrapper>
    );
};
